import axios, { AxiosInstance, AxiosRequestHeaders } from "axios";
import { conf } from "../utils/config";
import { Novedad } from "../types/novedad";
import { NovedadesProgramadasPaginatedResponse } from "../interfaces/novedadesProgramadas";
import { NovedadesMasivasPaginatedResponse } from "../interfaces/novedadesMasivas";

// Timeout corto para los pings de /version: evita que un worker caído
// bloquee el dashboard por el timeout default (sin límite) de axios.
const VERSION_REQUEST_TIMEOUT_MS = 8000;
const VERSION_REQUEST_CONFIG = { timeout: VERSION_REQUEST_TIMEOUT_MS } as const;

// Configuración de todas las instancias de axios
const axiosInstancesConfig = {
  backApiAxios: "BACKEND_API_URL",
  backNotificationAxios: "BACKEND_NOTIFICATION_URL",
  backProxyAxios: "BACKEND_PROXY_URL",
  backSyncAxios: "BACKEND_SYNC_URL",
  backTxAxios: "BACKEND_TX_URL",
  backEventosAxios: "BACKEND_EVENTOS_URL",
  backOfertaAxios: "BACKEND_OFERTA_URL",
  backCuposAxios: "BACKEND_CUPOS_URL",
  backNovedadesAxios: "BACKEND_NOVEDADES_URL",
  backValidacionesAxios: "BACKEND_VALIDACIONES_URL",
  backUpdateCuposAxios: "BACKEND_UPDATE_CUPOS_URL",
  backDashboardAxios: "BACKEND_DASHBOARD_URL",
  backLoginAxios: "BACKEND_LOGIN_URL",
  backFijadasAxios: "BACKEND_FIJADAS_URL",
  backInscriptasAxios: "BACKEND_INSCRIPTAS_URL",
  backUpdateOfertaAxios: "BACKEND_UPDATE_OFERTA_URL",
  backPrioridadAxios: "BACKEND_PRIORIDAD_URL",
} as const;

// Crear todas las instancias de axios de forma dinámica
const axiosInstances = Object.entries(axiosInstancesConfig).reduce(
  (acc, [instanceName, envKey]) => {
    acc[instanceName as keyof typeof axiosInstancesConfig] = axios.create({
      baseURL: conf(envKey),
    });
    return acc;
  },
  {} as Record<keyof typeof axiosInstancesConfig, AxiosInstance>
);

// Extraer las instancias individuales para mantener compatibilidad con el código existente
const {
  backApiAxios,
  backNotificationAxios,
  backSyncAxios,
  backTxAxios,
  backEventosAxios,
  backOfertaAxios,
  backCuposAxios,
  backNovedadesAxios,
  backValidacionesAxios,
  backUpdateCuposAxios,
  backDashboardAxios,
  backLoginAxios,
  backFijadasAxios,
  backInscriptasAxios,
  backUpdateOfertaAxios,
  backPrioridadAxios,
} = axiosInstances;

// Debug: Verificar que las URLs se estén leyendo correctamente
const backendUrls = Object.entries(axiosInstancesConfig).reduce(
  (acc, [instanceName, envKey]) => {
    const key = envKey.replace("BACKEND_", "").replace("_URL", "");
    acc[key] = conf(envKey);
    return acc;
  },
  {} as Record<string, string | undefined>
);
console.log("Backend URLs configuradas:", backendUrls);

// Debug: Verificar que las instancias tengan baseURL configurado
const instancesBaseURLs = Object.entries(axiosInstances).reduce(
  (acc, [instanceName, instance]) => {
    acc[instanceName] = instance.defaults.baseURL;
    return acc;
  },
  {} as Record<string, string | undefined>
);
console.log("Instancias axios configuradas:", instancesBaseURLs);

// Función helper para configurar interceptores en instancias de axios
const setupAxiosInstance = (instance: AxiosInstance, requireToken: boolean = true) => {
  // Guardar el baseURL original al momento de la creación en un closure
  const originalBaseURL = instance.defaults.baseURL;

  // Marcar esta instancia para que los interceptores globales la reconozcan
  (instance as any).__isCustomInstance = true;
  (instance as any).__originalBaseURL = originalBaseURL;

  // Agregar interceptor que SIEMPRE establece el baseURL original
  // Este interceptor se ejecuta ANTES de los interceptores globales
  instance.interceptors.request.use(
    (config) => {
      // Marcar esta petición como proveniente de una instancia personalizada
      // Esto es crítico para que los interceptores globales la reconozcan
      (config as any).__isCustomInstance = true;
      (config as any).__originalBaseURL = originalBaseURL;

      // SIEMPRE establecer el baseURL original de la instancia
      // Si originalBaseURL es undefined, mantenerlo así (pero el interceptor global no debería modificarlo)
      if (originalBaseURL) {
        config.baseURL = originalBaseURL;
      }

      // Agregar token solo si se requiere
      if (requireToken) {
        const token = localStorage.getItem("token");
        if (token) {
          config.headers = {
            ...config.headers,
            Authorization: "Bearer " + token,
            Accept: "application/json",
            "Content-Type": "application/json",
          } as AxiosRequestHeaders;
        }
      }
      return config;
    },
    (error) => {
      return Promise.reject(error);
    }
  );
};

// Configurar interceptores para todas las instancias
// backNotificationAxios no requiere token
Object.entries(axiosInstances).forEach(([instanceName, instance]) => {
  const requireToken = instanceName !== "backNotificationAxios";
  setupAxiosInstance(instance, requireToken);
});

export const dashboardAPI = {
  syncronizarAlumnos: async (
    identificacion: number | null,
    programa: number | null,
    orientacion: number | null
  ) => {
    const response: any = await axios.post(`/dashboard/syncronizarAlumnos`, {
      identificacion: identificacion,
      programa: programa,
      orientacion: orientacion,
    });
    return response.data;
  },
  syncronizarAlumnosPorPromocion: async (
    identificacion: number | null,
    programa: number | null,
    orientacion: number | null,
    promocion: number | null,
    mes: number | null
  ) => {
    const response: any = await axios.post(
      `/dashboard/inicializarAlumnosByPromocionIPO`,
      {
        identificacion: identificacion,
        programa: programa,
        orientacion: orientacion,
        promocion: promocion,
        mes: mes,
      }
    );
    return response.data;
  },
  postVencerOfertas: async (
    identificacion: number | null,
    programa: number | null,
    orientacion: number | null
  ) => {
    const response: any = await axios.post(`/dashboard/vencerOfertasIPO`, {
      identificacion: identificacion,
      programa: programa,
      orientacion: orientacion,
    });
    return response.data;
  },
  postBorrarIPO: async (
    identificacion: number | null,
    programa: number | null,
    orientacion: number | null
  ) => {
    const response: any = await axios.post(`/dashboard/borrarIPO`, {
      identificacion: identificacion,
      programa: programa,
      orientacion: orientacion,
    });
    return response.data;
  },
  postResetearIPO: async (
    identificacion: number | null,
    programa: number | null,
    orientacion: number | null,
    resetearCursosFijados: boolean = false
  ) => {
    const response: any = await axios.post(`/dashboard/resetearIPO`, {
      identificacion: identificacion,
      programa: programa,
      orientacion: orientacion,
      resetearCursosFijados: resetearCursosFijados,
    });
    return response.data;
  },
  postInvalidarOfertas: async (
    identificacion: number | null,
    programa: number | null,
    orientacion: number | null
  ) => {
    const response: any = await axios.post(`/dashboard/invalidarOfertasIPO`, {
      identificacion: identificacion,
      programa: programa,
      orientacion: orientacion,
    });
    return response.data;
  },
  sincronizarSiguienteTabla: async () => {
    const response: any = await axios.post(
      `/dashboard/inicializarSiguienteTabla`,
      {}
    );
    return response.data;
  },
  inicializarConfiguracion: async () => {
    const response: any = await axios.post(`/dashboard/resetConfiguracion`, {});
    return response.data;
  },
  buildIndexes: async () => {
    const response: any = await axios.post(`/dashboard/db/buildIndexes`);
    return response.data;
  },
  syncInfoTablas: async () => {
    const response: any = await axios.post(`/dashboard/syncInfoTablas`);
    return response.data;
  },
  borrarTodosLosAlumnos: async () => {
    const response: any = await axios.post(
      `/dashboard/borrarTodosLosAlumnos`,
      {}
    );
    return response.data;
  },
  alumnosInicializados: async () => {
    const response: any = await axios.post(
      `/dashboard/alumnosInicializados?r=${Math.random().toString()}`,
      {}
    );
    return response.data;
  },
  inicializarAlumno: async (email: string) => {
    const response: any = await axios.post(`/dashboard/inicializarAlumno`, {
      email,
    });
    return response.data;
  },
  obtenerDetalleAlumno: async (idAlumnoPrograma: number) => {
    const response: any = await axios.post(`/dashboard/detalleAlumno`, {
      idAlumnoPrograma,
    });
    return response.data;
  },
  inicializarTablasMirror: async () => {
    const response: any = await axios.post(
      `/dashboard/inicializarTablasMirror`,
      {}
    );
    return response.data;
  },
  resetearTareasCronMirror: async () => {
    const response: any = await axios.post(
      `/dashboard/sync/resetearTareasCronMirror`,
      {}
    );
    return response.data;
  },
  inicializarTablaMirror: async (tabla: string, usarUpsertHash?: boolean) => {
    const response: any = await axios.post(
      `/dashboard/inicializarTablasMirror`,
      usarUpsertHash ? { tabla, usarUpsertHash: true } : { tabla }
    );
    return response.data;
  },
  resetInfoAlumnos: async () => {
    const response: any = await axios.post(`/dashboard/resetInfoAlumnos`, {});
    return response.data;
  },
  deleteAllTables: async () => {
    const response: any = await axios.post(`/dashboard/deleteAllTables`, {});
    return response.data;
  },
  dropAllTables: async () => {
    const response: any = await axios.post(`/dashboard/dropAllTables`, {});
    return response.data;
  },
  actualizarCupos: async () => {
    const response = await axios.post(`/dashboard/actualizarTodosLosCupos`, {});
    return response.data;
  },
  actualizarCupo: async (idCupo: number) => {
    const response = await axios.post(`/dashboard/actualizarCupo`, { idCupo });
    return response.data;
  },
  syncCuposDescuadrados: async () => {
    const response = await axios.post(`/dashboard/syncCuposDescuadrados`, {});
    return response.data;
  },
  actualizarSincronizacionTablasMirror: async () => {
    const response = await axios.post(
      `/dashboard/sync/actualizarSincronizacionTablasMirror`,
      {}
    );
    return response.data;
  },
  estadoTablasMirror: async () => {
    const response = await axios.post(`/dashboard/estadoTablasMirror`, {});
    return response.data;
  },
  checkSyncTablasMirror: async () => {
    const response = await axios.post(`/dashboard/checkSyncTablasMirror`, {});
    return response.data;
  },
  actualizarSincronizacionTabla: async (tabla: string) => {
    const response = await axios.post(
      `/dashboard/actualizarSincronizacionTabla`,
      {
        tabla,
      }
    );
    return response.data;
  },
  checkSyncCountTablaMirror: async (tabla: string) => {
    const response = await axios.post(`/dashboard/checkSyncTablaMirror`, {
      tabla,
    });
    return response.data;
  },
  dispararSyncTablaMirror: async (tabla: string, usarUpsertHash?: boolean) => {
    const response = await axios.post(`/dashboard/dispararSyncTablaMirror`, {
      tabla,
      usarUpsertHash: !!usarUpsertHash,
    });
    return response.data;
  },
  /**
   * Omitir marcadorSincronizacion equivale a asumir que la descarga ya arrancó, que es lo que
   * corresponde al reanudar el seguimiento de un run disparado en otra visita a la pantalla.
   */
  progresoSyncTablaMirror: async (
    tabla: string,
    marcadorSincronizacion?: string | null,
    usarUpsertHash?: boolean
  ) => {
    const response = await axios.post(`/dashboard/progresoSyncTablaMirror`, {
      tabla,
      marcadorSincronizacion,
      usarUpsertHash: !!usarUpsertHash,
    });
    return response.data;
  },
  getDetalleCuadratura: async () => {
    const response = await axios.post(`/dashboard/getDetalleCuadratura`, {});
    return response.data;
  },
  procesarCuadratura: async () => {
    const response = await axios.post(`/dashboard/procesarCuadratura`, {});
    return response.data;
  },
  obtenerDetalleCupo: async (idCupo: number) => {
    const response = await axios.post(`/dashboard/detalleCupo`, {
      idCupo,
    });
    return response.data;
  },
  obtenerDetalleCurso: async (idCurso: number, anioLectivo: number) => {
    const response = await axios.post(`/dashboard/detalleCurso`, {
      idCurso: Number(idCurso),
      anioLectivo: Number(anioLectivo),
    });
    return response.data;
  },
  resetearAlumno: async (idPersona: number, deleteFijadas: boolean) => {
    const response = await axios.post(`/dashboard/resetearAlumno`, {
      idPersona,
      deleteFijadas,
    });
    return response.data;
  },
  eliminarAlumno: async (idPersona: number) => {
    const response = await axios.post(`/dashboard/eliminarAlumno`, {
      idPersona,
    });
    return response.data;
  },
  invalidarSemaforo: async (idPersona: number) => {
    const response = await axios.post(`/dashboard/invalidarSemaforo`, {
      idPersona,
    });
    return response.data;
  },
  resetearAlumnosRegistrados: async (
    resetearCursosFijados: boolean = false
  ) => {
    const response = await axios.post(`/dashboard/resetearAlumnosRegistrados`, {
      resetearCursosFijados,
    });
    return response.data;
  },
  invalidarSemaforoGlobal: async () => {
    const response = await axios.post(`/dashboard/invalidarSemaforoGlobal`);
    return response.data;
  },
  vencerOferta: async (idAlumnoPrograma: number) => {
    const response = await axios.post(`/dashboard/vencerOferta`, {
      idAlumnoPrograma,
    });
    return response.data;
  },
  invalidarOferta: async (idAlumnoPrograma: number) => {
    const response = await axios.post(`/dashboard/invalidarOferta`, {
      idAlumnoPrograma,
    });
    return response.data;
  },
  invalidarOfertaGlobal: async () => {
    const response = await axios.post(`/dashboard/invalidarOfertaGlobal`);
    return response.data;
  },
  vencerOfertaGlobal: async () => {
    const response = await axios.post(`/dashboard/vencerOfertaGlobal`);
    return response.data;
  },
  actualizarOferta: async (idAlumnoPrograma: number) => {
    const response = await axios.post(`/dashboard/actualizarOferta`, {
      idAlumnoPrograma,
    });
    return response.data;
  },
  actualizarInscripciones: async (idAlumnoPrograma: number) => {
    const response = await axios.post(`/dashboard/actualizarInscripciones`, {
      idAlumnoPrograma,
    });
    return response.data;
  },
  obtenerAlumnosRegistrados: async () => {
    const response = await axios.post(`/dashboard/alumnosRegistrados`, {});
    return response.data;
  },
  obtenerAlumnosRegistradosPaginado: async (
    limit: number,
    offset: number,
    carrera?: string,
    searchText?: string,
    estadoAlumnoId?: string,
    conectado?: string
  ) => {
    const response = await axios.post(`/dashboard/alumnosRegistradosPag`, {
      limit,
      offset,
      carrera: carrera || "",
      searchText: searchText || "",
      estadoAlumnoId,
      conectado,
    });
    return response.data;
  },
  buscarNovedadesOracle: async () => {
    const response = await axios.get(`/dashboard/buscarNovedadesOracle`);
    return response.data;
  },
  obtenerReservasEnTransito: async () => {
    const response = await axios.get(`/dashboard/reservasEnTransito`);
    return response.data;
  },
  obtenerDesinscripcionEnTransito: async () => {
    const response = await axios.get(`/dashboard/desinscripcionesEnTransito`);
    return response.data;
  },
  validateToken: async (token: string) => {
    // Crear una instancia de axios sin interceptores para evitar usar el token del localStorage
    const validateAxios = axios.create({
      baseURL: conf("BACKEND_API_URL"),
    });

    const response = await validateAxios.post(
      `/dashboard/validateToken?r=${Math.random().toString()}`,
      {},
      {
        headers: {
          Authorization: `Bearer ${token}`,
          Accept: "application/json",
          "Content-Type": "application/json",
        },
      }
    );
    return response.data;
  },
  obtenerParametros: async () => {
    const response = await axios.get(`/dashboard/parametros`);
    return response.data;
  },
  updateParametro: async (parametro: string, value: string) => {
    const response = await axios.put(`/dashboard/parametro`, {
      parametro,
      value,
    });
    return response.data;
  },
  obtenerValidaciones: async () => {
    const response = await axios.get(`/dashboard/validaciones`);
    return response.data;
  },
  inicializarValidaciones: async () => {
    const response = await axios.get(`/dashboard/inicializarValidaciones`);
    return response.data;
  },
  updateValidacion: async (
    activa: string | null,
    configuracion: string | null,
    id: string
  ) => {
    const response = await axios.put(`/dashboard/validacion`, {
      activa,
      configuracion,
      id,
    });
    return response.data;
  },
  obtenerDetallesDeColas: async () => {
    const response = await axios.get(`/dashboard/queues`);
    return response.data;
  },
  logoutGeneral: async () => {
    const response = await axios.get(`/dashboard/logoutGeneral`);
    return response.data;
  },
  logoutIndividual: async (idPersona: number) => {
    const response = await axios.post(`/dashboard/logoutIndividual`, {
      idPersona,
    });
    return response.data;
  },
  getVersionBackApi: async (clon: number | null) => {
    const response = await backApiAxios.get(`/version`, VERSION_REQUEST_CONFIG);
    return response.data;
  },
  getVersionBackSync: async (clon: number | null) => {
    const response = await backSyncAxios.get(`/version`, VERSION_REQUEST_CONFIG);
    return response.data;
  },
  getVersionBackNotification: async () => {
    const response = await backNotificationAxios.get(`/version`, VERSION_REQUEST_CONFIG);
    return response.data;
  },
  getVersionBackProxy: async () => {
    const response = await axios.get(`/dashboard/proxy-version`, VERSION_REQUEST_CONFIG);
    return response.data;
  },
  getVersionBackTx: async () => {
    const response = await backTxAxios.get(`/version`, VERSION_REQUEST_CONFIG);
    return response.data;
  },
  getVersionBackEventos: async () => {
    const response = await backEventosAxios.get(`/version`, VERSION_REQUEST_CONFIG);
    return response.data;
  },
  getVersionBackOferta: async () => {
    const response = await backOfertaAxios.get(`/version`, VERSION_REQUEST_CONFIG);
    return response.data;
  },
  getVersionBackCupos: async () => {
    const response = await backCuposAxios.get(`/version`, VERSION_REQUEST_CONFIG);
    return response.data;
  },
  getVersionBackNovedades: async () => {
    const response = await backNovedadesAxios.get(`/version`, VERSION_REQUEST_CONFIG);
    return response.data;
  },
  getVersionBackValidaciones: async () => {
    const response = await backValidacionesAxios.get(`/version`, VERSION_REQUEST_CONFIG);
    return response.data;
  },
  getVersionBackMultipleInscripciones: async (workerNumber: number) => {
    const url = conf(`BACKEND_MULTIPLES_INSCRIPCIONES_URL`)?.replace(
      "x",
      workerNumber.toString()
    );
    const response = await axios.get(`${url}/version`, VERSION_REQUEST_CONFIG);
    return response.data;
  },
  getVersionBackMultipleValidaciones: async (workerNumber: number) => {
    const url = conf(`BACKEND_MULTIPLES_VALIDACIONES_URL`)?.replace(
      "x",
      workerNumber.toString()
    );
    const response = await axios.get(`${url}/version`, VERSION_REQUEST_CONFIG);
    return response.data;
  },
  getVersionBackMultipleCupos: async (workerNumber: number) => {
    const url = conf(`BACKEND_MULTIPLES_CUPOS_URL`)?.replace(
      "x",
      workerNumber.toString()
    );
    const response = await axios.get(`${url}/version`, VERSION_REQUEST_CONFIG);
    return response.data;
  },
  getVersionBackMultipleOferta: async (workerNumber: number) => {
    const url = conf(`BACKEND_MULTIPLES_OFERTA_URL`)?.replace(
      "x",
      workerNumber.toString()
    );
    const response = await axios.get(`${url}/version`, VERSION_REQUEST_CONFIG);
    return response.data;
  },
  getVersionBackUpdateCupos: async () => {
    const response = await backUpdateCuposAxios.get(`/version`, VERSION_REQUEST_CONFIG);
    return response.data;
  },
  getVersionBackDashboard: async () => {
    const response = await backDashboardAxios.get(`/version`, VERSION_REQUEST_CONFIG);
    return response.data;
  },
  getVersionBackLogin: async () => {
    const response = await backLoginAxios.get(`/version`, VERSION_REQUEST_CONFIG);
    return response.data;
  },
  getVersionBackFijadas: async () => {
    const response = await backFijadasAxios.get(`/version`, VERSION_REQUEST_CONFIG);
    return response.data;
  },
  getVersionBackInscriptas: async () => {
    const response = await backInscriptasAxios.get(`/version`, VERSION_REQUEST_CONFIG);
    return response.data;
  },
  getVersionBackUpdateOferta: async () => {
    const response = await backUpdateOfertaAxios.get(`/version`, VERSION_REQUEST_CONFIG);
    return response.data;
  },
  getVersionBackPrioridad: async () => {
    const response = await backPrioridadAxios.get(`/version`, VERSION_REQUEST_CONFIG);
    return response.data;
  },
  getCarreras: async () => {
    const response = await axios.get(`/dashboard/carrerasList`);
    return response.data;
  },
  getNovedadesPaginated: async (
    limit: number,
    offset: number,
    estadoFilterEnable: boolean,
    filterProcessedWithError: boolean,
    id: number | null
  ) => {
    const response = await axios.post(`/dashboard/novedades`, {
      limit,
      offset,
      estadoFilterEnable,
      filterProcessedWithError,
      id,
    });
    return response.data;
  },
  reprocesarNovedadesConError: async () => {
    const response = await axios.post(`/dashboard/reprocesarNovedadesConError`);
    return response.data;
  },
  descartarNovedad: async (novedad: Novedad) => {
    const response = await axios.post(`/descartarNovedad`, {
      novedad: novedad,
    });
    return response.data;
  },
  getTransaccionEnTransito: async (limit: number, offset: number) => {
    const response = await axios.post(`/dashboard/transaccionesEnTransito`, {
      limit,
      offset,
    });
    return response.data;
  },
  getInscripcionesEnTransito: async (limit: number, offset: number) => {
    const response = await axios.post(`/dashboard/inscripcionesEnTransito`, {
      limit,
      offset,
    });
    return response.data;
  },
  cancelarInscripcionesEnTransitoXId: async (ids: number[]) => {
    const response = await axios.post(
      `/dashboard/cancelarInscripcionesEnTransitoXId`,
      {
        ids,
      }
    );
    return response;
  },
  cancelarDesinscripcionesEnTransitoXId: async (ids: number[]) => {
    const response = await axios.post(
      `/dashboard/cancelarDesinscripcionesEnTransitoXId`,
      {
        ids,
      }
    );
    return response;
  },
  getDesinscripcionesEnTransito: async (limit: number, offset: number) => {
    const response = await axios.post(`/dashboard/desinscripcionesEnTransito`, {
      limit,
      offset,
    });
    return response.data;
  },
  procesarTransaccionesPorLote: async (idList: any[]) => {
    const response = await axios.post(
      `/dashboard/inscribirDesinscribirPorLote`,
      { idList }
    );
    return response.data;
  },
  delTransacciones: async () => {
    const response = await axios.post(`/dashboard/borrarTransacciones`, {});
    return response.data;
  },
  delEventos: async () => {
    const response = await axios.post(`/dashboard/borrarEventos`, {});
    return response.data;
  },
  exportEventos: async (fechaDesde: string, fechaHasta: string) => {
    const response = await axios.post(`/dashboard/exportEventos`, {
      fechaDesde,
      fechaHasta,
    });
    return response.data;
  },
  getEventosPaginated: async (
    pageSize: number,
    page: number,
    tipoEvento: string | null,
    idTx: number | null,
    componente: string | null,
    idCupo: number | null,
    idAlumnoPrograma: number | null,
    fechaDesde: string | null,
    fechaHasta: string | null,
    nivelEvento: string | null,
    descripcion: string | null
  ) => {
    try {
      const response = await axios.post(`/dashboard/eventos`, {
        limit: pageSize,
        offset: page,
        tipoEvento,
        idTx,
        componente,
        idCupo,
        idAlumnoPrograma,
        fechaDesde,
        fechaHasta,
        nivelEvento,
        descripcion,
      });
      return response.data;
    } catch (error) {
      console.error("Error fetching eventos:", error);
      throw error;
    }
  },
  exportTransacciones: async (fecha: number, hora: number | null) => {
    const response = await axios.post(`/dashboard/exportTransacciones`, {
      fecha,
      hora,
    });
    return response.data;
  },
  getEstadoGlobalTransacciones: async (
    fechaInicio: string,
    fechaFin: string
  ) => {
    const response = await axios.post(`/dashboard/estadoGlobalTx`, {
      fechaInicio,
      fechaFin,
    });
    return response.data;
  },
  getTransaccionesPaginated: async (
    limit: number,
    offset: number,
    fecha: number,
    hora: number | null,
    estado: string | null,
    idTx: number | null,
    tipoTx: string | null,
    idAlumnoPrograma: number | null,
    idCurso: number | null,
    idCupo: number | null
  ) => {
    const response = await axios.post(`/dashboard/transacciones`, {
      limit,
      offset,
      fecha,
      hora,
      estado: estado || null,
      idTx: idTx || null,
      tipoTx: tipoTx || null,
      idAlumnoPrograma: idAlumnoPrograma || null,
      idCurso: idCurso || null,
      idCupo: idCupo || null,
    });
    return response.data;
  },
  deleteNovedad: async (idNovedad: number) => {
    const response = await axios.delete(`/dashboard/novedad`, {
      data: { idNovedad },
    });
    return response.data;
  },
  deleteNovedades: async () => {
    const response = await axios.delete(`/dashboard/novedades`);
    return response.data;
  },
  reprocesarNovedad: async (novedadId: number) => {
    const response = await axios.post(`/dashboard/reprocesarNovedad`, {
      novedadId,
    });
    return response.data;
  },
  getNovedadesProgramadasPaginated: async (
    limit: number,
    offset: number,
    estado: number,
    id: number | null,
    idNovedadOrigen: number | null
  ) => {
    const response = await axios.post(`/dashboard/programadas/paginas`, {
      limit,
      offset,
      estado,
      id,
      idNovedadOrigen,
    });
    return response.data as NovedadesProgramadasPaginatedResponse;
  },
  getNovedadesMasivasPaginated: async (
    limit: number,
    offset: number,
    estado: string,
    idNovedadInicio: number | null,
    idNovedadFin: number | null
  ) => {
    const response = await axios.post(`/dashboard/masivas/paginas`, {
      limit,
      offset,
      estado,
      idNovedadInicio,
      idNovedadFin,
    });
    return response.data as NovedadesMasivasPaginatedResponse;
  },
  reprocesarNovedadMasiva: async (idNovedadMasiva: number) => {
    const response = await axios.post(`/dashboard/masivas/reprocesarMasiva`, {
      idNovedadMasiva,
    });
    return response.data;
  },
  regenerarNovedadesProgramadasPrioridad: async () => {
    const response = await axios.post(
      `/dashboard/regenerarNovedadesProgramadasPrioridad`
    );
    return response.data;
  },
  getEventosInscripcionPaginados: async (
    limit: number,
    offset: number,
    tipoEvento: string,
    idTx: number,
    idCupo: number,
    idAlumnoPrograma: number,
    fechaDesde: string,
    fechaHasta: string,
    nivelEvento: string
  ) => {
    try {
      const response = await axios.post(`/dashboard/evento/search`, {
        limit,
        offset,
        tipoEvento,
        idTx,
        idCupo,
        idAlumnoPrograma,
        fechaDesde,
        fechaHasta,
        nivelEvento,
      });
      return response.data;
    } catch (error) {
      console.error(
        "Error fetching eventos from /dashboard/evento/search:",
        error
      );
      throw error;
    }
  },
  getTiposEventos: async (): Promise<string[]> => {
    const response = await axios.get(`/dashboard/tiposEventos`);
    return response.data;
  },
  getTiposEventoSoporte: async (): Promise<string[]> => {
    const response = await axios.get(`/dashboard/tiposEventoSoporte`);
    return response.data;
  },
  inicializarAlumnosByIdPersonaList: async (idPersonaList: number[]) => {
    const response = await axios.post(
      `/dashboard/inicializarAlumnosByIdPersonaList`,
      {
        idPersonaList,
      }
    );
    return response.data;
  },
  depurarParametrosObsoletos: async () => {
    const response = await axios.post(`/dashboard/depurarParametrosObsoletos`);
    return response.data;
  },
  obtenerParametrosKeys: async () => {
    const response = await axios.get(`/dashboard/obtenerKeysParametros`);
    return response.data;
  },
  depurarCoreCupos: async () => {
    const response = await axios.post(`/dashboard/depurarCoreCupos`);
    return response.data;
  },
  depurarCoreKVInfoCursoOferta: async () => {
    const response = await axios.post(
      `/dashboard/depurarCoreKVInfoCursoOferta`
    );
    return response.data;
  },
  sincronizarTablasMirror: async () => {
    const response = await axios.post(
      `/dashboard/cron/sincronizarTablasMirror`
    );
    return response.data;
  },
  sincronizarInfoCursosyCupos: async () => {
    const response = await axios.post(
      `/dashboard/cron/sincronizarInfoCursosyCupos`
    );
    return response.data;
  },
  ejecutarNovedadesProgramadas: async () => {
    const response = await axios.post(
      `/dashboard/cron/ejecutarNovedadesProgramadas`
    );
    return response.data;
  },
  revisarSincronizacionMirrors: async () => {
    const response = await axios.post(
      `/dashboard/cron/revisarSincronizacionMirror`
    );
    return response.data;
  },
};
